/**
 * AlertHighlight — dashboard banner for the top AI-relevance alert.
 *
 * Behaviour:
 * - Fetches GET /api/alerts/highlight (returns highest-scoring alert of the
 *   last 7 days with score >= 60 that this user hasn't dismissed this week).
 * - Renders nothing if the endpoint returns {alert: null}.
 * - Two CTAs:
 *     1. If impact analysis exists but no draft CC → "Draft change control"
 *        (POST /api/alerts/draft-change-control)
 *     2. If no impact analysis → "Analyze impact" (deep-links to /alerts)
 *     3. If a draft CC already exists → "Open draft" (deep-links to /changes)
 * - Dismiss button posts to /api/alerts/highlight/dismiss which stores the
 *   alert_id under `user.dismissed_alert_highlights[YYYY-WNN]`, so the
 *   banner reappears automatically next ISO week.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AlertTriangle, GitBranch, Sparkles, X, ExternalLink, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import logger from "@/lib/logger";

export default function AlertHighlight() {
  const [highlight, setHighlight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drafting, setDrafting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/alerts/highlight")
      .then(({ data }) => setHighlight(data.alert))
      .catch((e) => logger.warn("alert highlight load:", e?.response?.status))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !highlight) return null;

  const score = highlight.ai_triage?.relevance_score;
  const category = highlight.ai_triage?.category || "Other";
  const rationale = highlight.ai_triage?.rationale || "";
  const scoreCls =
    score >= 80 ? "bg-red-100 text-red-800 border-red-300"
    : score >= 60 ? "bg-amber-100 text-amber-800 border-amber-300"
    : "bg-blue-100 text-blue-800 border-blue-300";

  const dismiss = async () => {
    setHighlight(null); // optimistic
    try {
      await api.post("/alerts/highlight/dismiss", { alert_id: highlight.alert_id });
      toast.success("Highlight dismissed for this week");
    } catch (e) {
      toast.error("Dismiss failed");
    }
  };

  const draftChange = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post("/alerts/draft-change-control",
        { alert_id: highlight.alert_id }, { timeout: 20000 });
      const chId = data.change_control?.change_id;
      toast.success(data.reused ? "Draft change control already exists" : "Draft change control created", {
        action: chId ? { label: "Open", onClick: () => navigate("/changes") } : undefined,
      });
      setHighlight({ ...highlight, change_control_id: chId });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Draft failed");
    } finally {
      setDrafting(false);
    }
  };

  const primaryCta = highlight.change_control_id ? (
    <Button
      onClick={() => navigate("/changes")}
      data-testid="alert-highlight-open-change"
      className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
    >
      <GitBranch className="w-3.5 h-3.5 mr-1.5" /> Open draft change <ArrowRight className="w-3.5 h-3.5 ml-1" />
    </Button>
  ) : highlight.has_impact_analysis ? (
    <Button
      onClick={draftChange}
      disabled={drafting}
      data-testid="alert-highlight-draft-change"
      className="h-9 bg-[#0055FF] hover:bg-[#0044cc] text-white font-semibold"
    >
      {drafting
        ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Drafting…</>
        : <><GitBranch className="w-3.5 h-3.5 mr-1.5" /> Draft change control</>}
    </Button>
  ) : (
    <Button
      onClick={() => navigate("/alerts")}
      data-testid="alert-highlight-analyze"
      className="h-9 bg-[#0055FF] hover:bg-[#0044cc] text-white font-semibold"
    >
      <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Analyze impact <ArrowRight className="w-3.5 h-3.5 ml-1" />
    </Button>
  );

  return (
    <div
      data-testid="alert-highlight-banner"
      className="mb-8 vq-card p-5 border-l-4 border-l-[#0055FF] bg-gradient-to-r from-slate-50 to-white"
    >
      <div className="flex items-start gap-4 flex-wrap">
        <div className="w-10 h-10 rounded bg-[#0055FF]/10 flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="w-5 h-5 text-[#0055FF]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="vq-overline text-[#0055FF]">Highest-relevance this week</span>
            <span
              data-testid="alert-highlight-score"
              className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border ${scoreCls}`}
              title={rationale}
            >
              <Sparkles className="w-3 h-3" /> AI {score} · {category}
            </span>
            <span className="text-[10px] font-mono text-slate-500 uppercase">{highlight.source}</span>
          </div>
          <h3
            data-testid="alert-highlight-title"
            className="font-display font-bold text-lg text-slate-900 line-clamp-2"
          >
            {highlight.title}
          </h3>
          {rationale && (
            <p className="text-xs text-slate-600 mt-1 line-clamp-2">
              <span className="font-mono uppercase text-[9px] tracking-wider text-slate-500">AI rationale · </span>
              {rationale}
            </p>
          )}
          <div className="flex items-center gap-2 flex-wrap mt-3">
            {primaryCta}
            {highlight.link && (
              <a
                href={highlight.link}
                target="_blank"
                rel="noreferrer"
                data-testid="alert-highlight-source"
                className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-[#0055FF] h-9 px-3"
              >
                Read source <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>
        <button
          onClick={dismiss}
          data-testid="alert-highlight-dismiss"
          aria-label="Dismiss for this week"
          title="Dismiss for this week — reappears with a fresh alert next week"
          className="p-2 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-800"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
